// @ts-nocheck
import { useState, useMemo } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Table,
  Badge,
  ActionIcon,
  Tooltip,
  Select,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconCheck,
  IconClock,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { useTimesheets, useClockIn, useClockOut } from '../../hooks/useTimeAttendance';
import { formatHours, formatTime } from '../../utils/format';

interface Punch {
  id: string;
  typeId: string;
  timeStamp: string;
  status: string;
  note: string | null;
}

interface TimesheetDay {
  date: string;
  totalHours: number;
  regularHours: number;
  overtimeHours: number;
  punches: Punch[];
}

interface Timesheet {
  employeeId: string;
  startDate: string;
  endDate: string;
  totalHours: number;
  totalRegular: number;
  totalOvertime: number;
  isVerified: boolean;
  days: TimesheetDay[];
}

function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatDateShort(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function toISODate(date: Date): string {
  return date.toISOString().split('T')[0] ?? '';
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function TimeAttendancePage() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [employeeFilter, setEmployeeFilter] = useState<string | null>(null);

  const today = new Date();
  const currentWeekStart = getWeekStart(today);
  const weekStart = new Date(currentWeekStart);
  weekStart.setDate(weekStart.getDate() + weekOffset * 7);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const { data, loading: isLoading } = useTimesheets({
    employeeId: employeeFilter || undefined,
    startDate: toISODate(weekStart),
    endDate: toISODate(weekEnd),
  });

  const timesheets = (data ?? []) as unknown as Timesheet[];

  const clockInMutation = useClockIn();
  const clockOutMutation = useClockOut();

  const weekDays = useMemo(() => {
    const days: string[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      days.push(toISODate(d));
    }
    return days;
  }, [weekStart.toISOString()]);

  const weekLabel = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  return (
    <Stack gap="md">
      <PageHeader
        title="Timesheets"
        subtitle="View and manage employee timesheets"
        breadcrumbs={[
          { label: 'Time & Attendance', to: '/admin/workforce/time' },
          { label: 'Timesheets' },
        ]}
        actions={
          <Group gap="sm">
            <Button
              variant="light"
              icon={<IconClock size={16} />}
              onClick={() => clockInMutation.mutate({})}
              loading={clockInMutation.isPending}
            >
              Clock In
            </Button>
            <Button
              variant="light"
              color="orange"
              icon={<IconClock size={16} />}
              onClick={() => clockOutMutation.mutate({})}
              loading={clockOutMutation.isPending}
            >
              Clock Out
            </Button>
          </Group>
        }
      />

      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" mb="md">
          <Group gap="sm">
            <ActionIcon variant="subtle" onClick={() => setWeekOffset((o) => o - 1)}>
              <IconChevronLeft size={18} />
            </ActionIcon>
            <Text fw={600} size="lg">
              {weekLabel}
            </Text>
            <ActionIcon variant="subtle" onClick={() => setWeekOffset((o) => o + 1)}>
              <IconChevronRight size={18} />
            </ActionIcon>
            {weekOffset !== 0 && (
              <Button variant="subtle" size="xs" onClick={() => setWeekOffset(0)}>
                Today
              </Button>
            )}
          </Group>

          <Select
            placeholder="All Employees"
            clearable
            value={employeeFilter}
            onChange={setEmployeeFilter}
            data={[]}
            w={200}
            searchable
          />
        </Group>

        {isLoading ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : timesheets.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            No timesheet data for this week.
          </Text>
        ) : (
          <Table striped highlightOnHover stickyHeader>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Employee</Table.Th>
                {weekDays.map((day, i) => (
                  <Table.Th key={day} ta="center" style={{ minWidth: 80 }}>
                    <Text size="xs" c="dimmed">
                      {DAY_NAMES[i]}
                    </Text>
                    <Text size="sm">{new Date(day + 'T12:00:00').getDate()}</Text>
                  </Table.Th>
                ))}
                <Table.Th ta="right">Total</Table.Th>
                <Table.Th ta="right">OT</Table.Th>
                <Table.Th ta="center" w={60}>
                  Status
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {timesheets.map((ts) => {
                const dayMap = new Map(ts.days.map((d) => [d.date, d]));
                return (
                  <Table.Tr key={ts.employeeId}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {ts.employeeId}
                      </Text>
                    </Table.Td>
                    {weekDays.map((day) => {
                      const dayData = dayMap.get(day);
                      const hours = dayData?.totalHours ?? 0;
                      const hasException = dayData?.punches.some((p) => p.status === 'exception');
                      return (
                        <Table.Td key={day} ta="center">
                          {hours > 0 ? (
                            <Tooltip
                              label={
                                dayData?.punches
                                  .map((p) => `${p.typeId}: ${formatTime(p.timeStamp)}`)
                                  .join(', ') || 'No punches'
                              }
                            >
                              <Text
                                size="sm"
                                fw={500}
                                c={hasException ? 'red' : hours > 8 ? 'orange' : undefined}
                              >
                                {formatHours(hours)}
                              </Text>
                            </Tooltip>
                          ) : (
                            <Text size="sm" c="dimmed">
                              -
                            </Text>
                          )}
                        </Table.Td>
                      );
                    })}
                    <Table.Td ta="right">
                      <Text size="sm" fw={600}>
                        {formatHours(ts.totalHours)}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      {ts.totalOvertime > 0 ? (
                        <Text size="sm" c="orange" fw={500}>
                          {formatHours(ts.totalOvertime)}
                        </Text>
                      ) : (
                        <Text size="sm" c="dimmed">
                          -
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td ta="center">
                      {ts.isVerified ? (
                        <Badge size="sm" color="green" variant="light">
                          <IconCheck size={12} />
                        </Badge>
                      ) : (
                        <Badge size="sm" color="yellow" variant="light">
                          <IconAlertTriangle size={12} />
                        </Badge>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}
